import { useTranslation } from "@/hooks";
import {
  Button,
  Center,
  Flex,
  Modal,
  Progress,
  Stack,
  Text,
} from "@mantine/core";
import { IconLoader } from "@tabler/icons-react";
import { useEffect, useState } from "react";

export function WorkspaceModalEnhancementLoading({
  total,
  completed,
  onCancel,
  serviceName,
  startTime,
}: {
  total: number;
  completed: number;
  onCancel?: () => void;
  serviceName?: string;
  startTime?: number;
}) {
  const { t } = useTranslation();
  const percentage =
    total > 0 ? Math.min(100, Math.round((completed / total) * 100)) : 0;
  const [elapsedTime, setElapsedTime] = useState(0);

  useEffect(() => {
    if (!startTime) return;

    const interval = setInterval(() => {
      setElapsedTime(Date.now() - startTime);
    }, 100); // Update every 100ms for smooth display

    return () => clearInterval(interval);
  }, [startTime]);

  const formatElapsedTime = (ms: number): string => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${seconds}s`;
  };

  return (
    <Modal
      opened={true}
      onClose={() => {}}
      title={t("enhancement").enhancingData}
      centered
      size="md"
      closeOnClickOutside={false}
      closeOnEscape={false}
      withCloseButton={false}
    >
      <Stack gap="md" p="md">
        {serviceName && (
          <Text size="sm" fw={500} ta="center" c="gray.9">
            {serviceName}
          </Text>
        )}

        <Center>
          <IconLoader
            size={48}
            style={{ animation: "spin 1s linear infinite" }}
          />
        </Center>

        <Stack gap="xs">
          <Text size="sm" c="gray.9" ta="center">
            {t("enhancement").processingRows.replace(
              "{count}",
              total.toLocaleString()
            )}
          </Text>
          <Progress value={percentage} size="lg" radius="md" animated striped />
          <Text size="xs" c="gray.7" ta="center">
            {completed.toLocaleString()} / {total.toLocaleString()} (
            {percentage}%)
          </Text>
        </Stack>

        {startTime && (
          <Text size="xs" c="gray.6" ta="center">
            Elapsed: {formatElapsedTime(elapsedTime)}
          </Text>
        )}

        {onCancel && (
          <Flex justify="center" mt="md">
            <Button variant="outline" color="red" onClick={onCancel}>
              {t("common").cancel}
            </Button>
          </Flex>
        )}
      </Stack>

      <style>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </Modal>
  );
}
